import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { notify } from '../../context/FeedbackContext';
import './LoginPage.css';
import axios from 'axios';

function LoginPage({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [forgotUsername, setForgotUsername] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter your username and password.');
      return;
    }

    setIsLoading(true);
    setError('');

    // ── DEV BYPASS: remove this block before connecting to real backend ──
    // if (username === 'psjoy' && password === 'Passw0rD3') {
    //   localStorage.setItem('authToken', 'dev-token');
    //   onLoginSuccess({ code: 200 });
    //   return;
    // }
    //── END DEV BYPASS ───────────────────────────────────────────────────

    try {
      const response = await fetch(buildApiUrl('/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (data.code === 200) {
        const token = data.data.token;
        // const tenantId = data.data.tenantId;
        const usernameStore = data.data.username;
        const role = data.data.role;
        const permissions = data.data.permissions;
        const permissionsMenu = data.data.permissionsMenu;

        if (permissions.includes('system:admin')) {
          // console.log(data);
          if (token) {
            localStorage.setItem('authToken', token);
            // localStorage.setItem('tenantId', tenantId);
            localStorage.setItem('username', usernameStore);
            localStorage.setItem('role', role);
            localStorage.setItem('permissions', JSON.stringify(permissions))
            localStorage.setItem('permissionsMenu', JSON.stringify(permissionsMenu))

          }
          onLoginSuccess(data);
        } else {
          notify('Please contact an administrator. Your account does not have permission.', 'error')

          localStorage.clear()
        }


      } else {
        const errorMessage = data.message || data.msg || (data.data && data.data.message) || 'Invalid username or password.';
        setError(errorMessage);
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Request activation code
  // Wire up to backend: POST /forgot-password with { user_name: forgotUsername }
  // On success (code 200): call setView('reset')
  // On error: call setForgotError(data.message)
  const handleForgotSubmit = async () => {
    if (!forgotUsername) {
      setForgotError('Please enter your username.');
      return;
    }
    setForgotError('');
    setIsLoading(true);

    try {
      const response = await fetch(buildApiUrl('/auth/gateway/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername }),
      });

      const data = await response.json();

      if (data.code === 200) {
        setView('reset');
      } else {
        setForgotError(data.message || data.msg || 'Failed to send code.');
      }

    } catch (err) {
      setForgotError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
    // TODO: connect to backend
  };

  // Step 2: Submit activation code + new password
  // Wire up to backend: POST /reset-password with { user_name: forgotUsername, activation_code: activationCode, new_password: newPassword }
  // On success (code 200): call backToLogin()
  // On error: call setForgotError(data.message)
  const handleResetSubmit = async () => {
    if (!activationCode) { setForgotError('Please enter the activation code.'); return; }
    if (!newPassword) { setForgotError('Please enter a new password.'); return; }
    if (newPassword !== confirmPassword) { setForgotError('Passwords do not match.'); return; }
    setForgotError('');
    setIsLoading(true);

    try {
      const response = await fetch(buildApiUrl('/auth/gateway/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activationCode: activationCode,
          newPassword: newPassword
        }),
      });

      const data = await response.json();

      if (data.code === 200) {
        backToLogin();
      } else {
        setForgotError(data.message || data.msg || 'Failed to reset password');
      }
    } catch (err) {
      setForgotError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const backToLogin = () => {
    setView('login');
    setForgotUsername('');
    setActivationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotError('');
  };

  // ── Forgot Password: Step 1 ──
  if (view === 'forgot') {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-brand">
              <img
                src="/PosjoyManagement.png"
                alt="Posjoy Management"
                className="login-brand-icon"
              />
              <span className="login-brand-text">Posjoy Management</span>
            </div>

            <h1 className="login-title">Forgot Password</h1>
            <p className="fp-subtitle">Enter your username and we'll send a reset code to your registered email.</p>

            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="forgot-username">Username</label>
                <div className="login-input-wrapper">
                  <input
                    id="forgot-username"
                    className="login-input"
                    type="text"
                    placeholder="Enter your username"
                    value={forgotUsername}
                    onChange={e => setForgotUsername(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            {forgotError && <p className="login-error">{forgotError}</p>}

            <button className="login-btn" onClick={handleForgotSubmit} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <p className="fp-back" onClick={backToLogin}>Back to Login</p>
          </div>
        </div>
        <footer className="login-footer">广州点线圆科技有限公司</footer>
      </div>
    );
  }

  // ── Forgot Password: Step 2 ──
  if (view === 'reset') {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-brand">
              <img
                src="/PosjoyManagement.png"
                alt="Posjoy Management"
                className="login-brand-icon"
              />
              <span className="login-brand-text">Posjoy Management</span>
            </div>

            <h1 className="login-title">Reset Password</h1>
            <p className="fp-subtitle">Enter the activation code sent to your email and set a new password.</p>

            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="reset-code">Activation Code</label>
                <div className="login-input-wrapper">
                  <input
                    id="reset-code"
                    className="login-input"
                    type="text"
                    placeholder="Enter activation code"
                    value={activationCode}
                    onChange={e => setActivationCode(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="reset-new-password">New Password</label>
                <div className="login-input-wrapper">
                  <input
                    id="reset-new-password"
                    className="login-input login-input-with-toggle"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(p => !p)}
                    tabIndex={-1}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>

            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="reset-confirm-password">Confirm New Password</label>
                <div className="login-input-wrapper">
                  <input
                    id="reset-confirm-password"
                    className="login-input login-input-with-toggle"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>

            {forgotError && <p className="login-error">{forgotError}</p>}

            <button className="login-btn" onClick={handleResetSubmit} disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>

            <p className="fp-back" onClick={() => setView('forgot')}>Back</p>
          </div>
        </div>
        <footer className="login-footer">广州点线圆科技有限公司</footer>
      </div>
    );
  }

  // ── Login ──
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-brand">
            <img
              src="/PosjoyManagement.png"
              alt="Posjoy Management"
              className="login-brand-icon"
            />
            <span className="login-brand-text">Posjoy Management</span>
          </div>

          <h1 className="login-heading">sign in</h1>
          <p className="login-subtitle">Enter your credentials to access your dashboard</p>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="login-username">Username</label>
                <div className="login-input-wrapper">
                  <input
                    id="login-username"
                    className="login-input"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                  />
                </div>
              </div>
            </div>

            <div className="login-field">
              <div className="login-input-box">
                <label className="login-label-inbox" htmlFor="login-password">Password</label>
                <div className="login-input-wrapper">
                  <input
                    id="login-password"
                    className="login-input login-input-with-toggle"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>

            <p className="login-forgot login-forgot-right" onClick={() => setView('forgot')}>
              Forgot password?
            </p>

            {error && <p className="login-error">{error}</p>}

            <button className="login-btn" type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <footer className="login-footer">广州点线圆科技有限公司</footer>
    </div>
  );
}

export default LoginPage;
